/**
 * Copyright (C) 2021 THL A29 Limited, a Tencent company.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import ParagraphBase from '@/core/ParagraphBase';

export default class SimpleTable extends ParagraphBase {
  static HOOK_NAME = 'simpleTable';

  constructor({ externals, config }) {
    super({ needCache: true });
    this.$lines = [];
  }

  $prevTdKey(curTdKey) {
    return curTdKey.replace(/^([0-9]+)-([0-9]+)$/, function (match, num1, num2) {
      return `${num1}-${+num2 - 1}`;
    });
  }

  $nextTdKey(curTdKey) {
    return curTdKey.replace(/^([0-9]+)-([0-9]+)$/, function (match, num1, num2) {
      return `${num1}-${+num2 + 1}`;
    });
  }

  $prevTrKey(curTrKey) {
    return curTrKey.replace(/^([0-9]+)-([0-9]+)$/, function (match, num1, num2) {
      return `${+num1 - 1}-${num2}`;
    });
  }

  $nextTrKey(curTrKey) {
    return curTrKey.replace(/^([0-9]+)-([0-9]+)$/, function (match, num1, num2) {
      return `${+num1 + 1}-${num2}`;
    });
  }

  $setColMapVal(autoMergeMap, curCellKey) {
    if (autoMergeMap[curCellKey] === undefined)
      return (autoMergeMap[curCellKey] = [1, 2]), (autoMergeMap[this.$nextTdKey(curCellKey)] = [1, -1]), autoMergeMap;
    if (-1 === autoMergeMap[curCellKey][1]) {
      const prevTdKey = this.$prevTdKey(curCellKey);
      return (autoMergeMap[this.$nextTdKey(curCellKey)] = [1, -1]), this.$setColMapVal(autoMergeMap, prevTdKey);
    }
    return (autoMergeMap[curCellKey][1] += 1), autoMergeMap;
  }

  $setRowMapVal(autoMergeMap, curCellKey) {
    if (autoMergeMap[curCellKey] === undefined)
      return (autoMergeMap[curCellKey] = [2, 1]), (autoMergeMap[this.$nextTrKey(curCellKey)] = [-1, 1]), autoMergeMap;
    const nextTrKey = this.$nextTrKey(curCellKey);
    if (
      ((autoMergeMap[nextTrKey] = autoMergeMap[nextTrKey] === undefined ? [1, 1] : autoMergeMap[nextTrKey]),
      autoMergeMap[curCellKey][1] !== autoMergeMap[nextTrKey][1])
    )
      return autoMergeMap;
    if (1 === autoMergeMap[curCellKey][0])
      return (autoMergeMap[curCellKey][0] = 2), (autoMergeMap[nextTrKey][0] = -1), autoMergeMap;
    if (-1 === autoMergeMap[curCellKey][0]) {
      const prevTrKey = this.$prevTrKey(curCellKey);
      return (autoMergeMap[nextTrKey][0] = -1), this.$setRowMapVal(autoMergeMap, prevTrKey);
    }
    return (autoMergeMap[curCellKey][0] += 1), autoMergeMap;
  }

  $handleMerge(nextKey, curKey, autoMergeMap, merge) {
    const isCurlMerged = this.$hasMerged(autoMergeMap, curKey);
    const isNextMerged = this.$hasMerged(autoMergeMap, nextKey);
    if (isCurlMerged || isNextMerged) {
      const canMerged = this.$canMerged(curKey, nextKey, autoMergeMap);
      if (canMerged) {
        const result = merge();
        return result;
      }
    } else {
      const result = merge();
      return result;
    }
    return false;
  }

  $dealColCell(rowIdx, colIdx, line, autoMergeMap) {
    const nextColIdx = +colIdx + 1;
    const nextCol = !!line[nextColIdx] && line[nextColIdx].trim();
    const nextColKey = this.$getCellKey(rowIdx, nextColIdx);
    const curCell = line[colIdx].trim();
    const curCellKey = this.$getCellKey(rowIdx, colIdx);
    const isSame = curCell === nextCol;
    const merge = () => this.$setColMapVal(autoMergeMap, curCellKey);
    if (isSame) {
      const result = this.$handleMerge(nextColKey, curCellKey, autoMergeMap, merge);
      if (result) return result;
    }
    return autoMergeMap;
  }

  $dealRowCell(rowIdx, colIdx, line, autoMergeMap) {
    const nextRowIdx = +rowIdx + 1;
    const nextRow = !(!line[nextRowIdx] || !line[nextRowIdx][colIdx]) && line[nextRowIdx][colIdx].trim();
    const nextRowKey = this.$getCellKey(nextRowIdx, colIdx);
    const curCell = line[rowIdx][colIdx].trim();
    const curCellKey = this.$getCellKey(rowIdx, colIdx);
    const isSame = curCell === nextRow;
    const merge = () => this.$setRowMapVal(autoMergeMap, curCellKey);
    if (isSame) {
      const result = this.$handleMerge(nextRowKey, curCellKey, autoMergeMap, merge);
      if (result) return result;
    }
    return autoMergeMap;
  }

  $getCellKey(num1, num2) {
    return `${num1}-${num2}`;
  }

  $getColAndRowSpanMap(lines) {
    let autoMergeMap = {};
    lines.forEach((line, rowIdx) => {
      line.forEach((tt, colIdx) => {
        autoMergeMap = this.$dealColCell(rowIdx, colIdx, line, autoMergeMap);
      });
    });
    lines.forEach((line, rowIdx, A) => {
      line.forEach((tt, colIdx) => {
        autoMergeMap = this.$dealRowCell(rowIdx, colIdx, lines, autoMergeMap);
      });
    });
    return autoMergeMap;
  }

  $convertTrsString2Array(lines) {
    const t = [];
    if (!lines) return lines;

    lines.forEach((line) => {
      line.length > 0 &&
        t.push(
          line
            .replace(/^\|{2,3}/, '')
            .replace(/\|\|\s*$/, '')
            .split('||'),
        );
    });
    return t;
  }

  $getTdContent(key) {
    const [col, row] = key.split('-');
    const content = this.$lines[col][row];
    return content.trim();
  }

  $getCellsMergeRange([[aStartCol, aStartRow], [aEndCol, aEndRow]], [[bStartCol, bStartRow], [bEndCol, bEndRow]]) {
    const startCol = Math.min(aStartCol, bStartCol);
    const startRow = Math.min(aStartRow, bStartRow);
    const endCol = Math.max(aEndCol, bEndCol);
    const endRow = Math.max(aEndRow, bEndRow);
    return [
      [startCol, startRow],
      [endCol, endRow],
    ];
  }

  $getMergedRange(key, autoMergeMap) {
    let [col, row] = key.split('-');
    const map = autoMergeMap[key];
    if (!map) {
      return [
        [col, row],
        [col, row],
      ];
    }
    let [fixCol, fixRow] = map;
    while (fixCol <= 0) {
      col = col - 1;
      [fixCol, fixRow] = autoMergeMap[`${col}-${row}`];
    }
    while (fixRow <= 0) {
      row = row - 1;
      [fixCol, fixRow] = autoMergeMap[`${col}-${row}`];
    }
    const startCol = col;
    const startRow = row;
    const endCol = +col + +fixCol - 1;
    const endRow = +row + +fixRow - 1;
    return [
      [startCol, startRow],
      [endCol, endRow],
    ];
  }

  $canMerged(nextKey, curKey, autoMergeMap) {
    const content = this.$getTdContent(curKey);
    const a = this.$getMergedRange(curKey, autoMergeMap);
    const b = this.$getMergedRange(nextKey, autoMergeMap);
    // @ts-ignore
    const [[startCol, startRow], [endCol, endRow]] = this.$getCellsMergeRange(a, b);
    // 判断是否每个元素的 content 是否都一样，如果有不一样的直接返回 false
    let currentCol = startCol;
    let currentRow = startRow;
    while (currentCol <= endCol) {
      while (currentRow <= endRow) {
        const key = `${currentCol}-${currentRow}`;
        const curContent = this.$getTdContent(key);
        if (curContent !== content) {
          return false;
        }
        currentRow = currentRow + 1;
      }
      currentCol = currentCol + 1;
      // 扫描完成以后重置指针位置
      currentRow = startRow;
    }
    return true;
  }

  $hasMerged(autoMergeMap, curSpanKey) {
    if (!autoMergeMap[curSpanKey]) return false;
    const hasMerged =
      autoMergeMap[curSpanKey][0] > 1 ||
      autoMergeMap[curSpanKey][1] > 1 ||
      autoMergeMap[curSpanKey][0] < 0 ||
      autoMergeMap[curSpanKey][1] < 0;
    return hasMerged;
  }

  $isMeerged(autoMergeMap, curSpanKey) {
    return !!autoMergeMap[curSpanKey] && (autoMergeMap[curSpanKey][0] < 0 || autoMergeMap[curSpanKey][1] < 0);
  }

  $getTdSpan(autoMergeMap, curSpanKey) {
    return autoMergeMap[curSpanKey] !== undefined
      ? `rowspan="${+autoMergeMap[curSpanKey][0] || 1}" colspan="${+autoMergeMap[curSpanKey][1] || 1}"`
      : 'rowspan="1" colspan="1"'; // 修复变更单元格内容时，单元格会异常合并的问题
  }

  $dealTh(str) {
    const thRightReg = /^\s*~T/.test(str);
    const thLeftReg = /~T\s*$/.test(str);
    const result = { style: '', content: '' };
    if (thRightReg && thLeftReg) {
      result.align = 'align="center"';
    } else if (thLeftReg) {
      result.align = 'align="left"';
    } else if (thRightReg) {
      result.align = 'align="right"';
    }
    result.content = str
      .replace(/^\s*~T/, '')
      .replace(/~T\s*$/, '')
      .trim();
    return result;
  }

  makeHtml(str, sentenceMakeFunc) {
    const self = this;
    const reg = /(?:(^|\n+))\s*?( *(>+ +)+)?((?:( *(>+ +)+)?\|\|[^\n]+($|\n))+)/g;
    const result = str.replace(reg, function (match, leading, prefix, _, sourceContent, __, ___, endfix) {
      const sign = self.$engine.md5(sourceContent);
      const linesCount = sourceContent.match(/\n/g).length;
      const isAutoMerge = /^\|\|\|/.test(sourceContent);
      const needTableHeader = /\|{2,3}\s*~T/.test(sourceContent) || /~T\s*\|\|/.test(sourceContent);
      // 处理引用嵌套表格
      let lines = sourceContent.split(/\n/).map((str) => str.replace(/(?:^|\n) *(>+) +/, ''));
      let headerStr = '';
      const trHtmlList = [];
      const tdHtmlList = [];
      if (needTableHeader && lines[0]) {
        headerStr = '<thead><tr>';

        lines[0]
          .replace(/^\|{2,3}/, '')
          .replace(/\|\|\s*$/, '')
          .split('||')
          .forEach((line) => {
            const thHtmlContent = self.$dealTh(line);
            tdHtmlList.push(thHtmlContent.align),
              (headerStr += `<th ${thHtmlContent.align}>${sentenceMakeFunc(thHtmlContent.content).html}</th>`);
          });
        (headerStr += '</tr></thead>'), lines.shift();
      }
      lines = self.$convertTrsString2Array(lines);

      self.$lines = lines;

      const autoMergeMap = isAutoMerge ? self.$getColAndRowSpanMap(lines) : {};

      lines.forEach((line, rowIdx) => {
        let trStr = '<tr>';

        line.forEach((cell, colIdx) => {
          const curSpanKey = self.$getCellKey(rowIdx, colIdx);
          if (!self.$isMeerged(autoMergeMap, curSpanKey))
            trStr += `<td ${tdHtmlList[colIdx] ? tdHtmlList[colIdx] : ''} ${self.$getTdSpan(
              autoMergeMap,
              curSpanKey,
            )}>${sentenceMakeFunc(cell.trim()).html}</td>`;
        });
        trStr += '</tr>';
        trHtmlList.push(trStr);
      });

      const tableStr = `<div class="cherry-table-container simple-table" data-lines="${linesCount}" data-sign="${sign}">\n        <table class="cherry-table">${
        headerStr + trHtmlList.join('')
      }</table></div>`;
      return `${leading}${prefix || ''}${self.pushCache(tableStr, sign, linesCount)}${endfix}`;
    });
    return result;
  }
}
